import { computed } from "vue"
import { useConfigStore } from "@core/stores/config.store"
import type { Supplier } from "@types"

/**
 * Composable centralisant l'accès aux fournisseurs d'énergie
 * avec lazy loading automatique (comme useSourceInfo)
 */
export function useSuppliers() {
    const configStore = useConfigStore()

    // ================================
    // COMPUTED - Lazy loading automatique
    // ================================

    /**
     * Récupère la liste des fournisseurs disponibles
     * Charge automatiquement les données si elles ne sont pas encore présentes
     */
    const getSuppliers = computed(() => {
        if (
            configStore.suppliers.length === 0 &&
            !configStore.isLoadingSuppliers &&
            !configStore.hasErrorSuppliers
        ) {
            configStore.fetchSuppliers()
        }
        return configStore.getSuppliers
    })

    // ================================
    // FONCTIONS HELPER
    // ================================

    /**
     * Récupère un fournisseur par son identifiant
     * Charge automatiquement les fournisseurs si nécessaire
     * @param _id_supplier - Identifiant du fournisseur
     * @returns Le fournisseur correspondant ou undefined si non trouvé
     */
    function getSupplier(_id_supplier: string | null): Supplier | undefined {
        if (
            configStore.suppliers.length === 0 &&
            !configStore.isLoadingSuppliers &&
            !configStore.hasErrorSuppliers
        ) {
            configStore.fetchSuppliers()
        }
        return configStore.getSupplierById(_id_supplier)
    }

    /**
     * Récupère le nom d'un fournisseur par son identifiant
     * @param _id_supplier - Identifiant du fournisseur
     * @returns Le nom du fournisseur ou une chaîne par défaut si non trouvé
     */
    function getSupplierName(
        _id_supplier: string | null | undefined,
        fallback = "-"
    ): string {
        if (!_id_supplier) return fallback
        const supplier = getSupplier(_id_supplier)
        return supplier?.name ?? fallback
    }

    // ================================
    // RETURN
    // ================================

    return {
        getSuppliers,
        getSupplier,
        getSupplierName,
        isLoadingSuppliers: computed(() => configStore.isLoadingSuppliers),
        hasErrorSuppliers: computed(() => configStore.hasErrorSuppliers),
        errorSuppliers: computed(() => configStore.errorSuppliers),
    }
}
